import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { publicApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const ComparePage = () => {
    const [comparisonTable, setComparisonTable] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchComparison = async () => {
            try {
                const res = await publicApi.getComparisons();
                if (res.data.length > 0) {
                    setComparisonTable(res.data[0]);
                }
            } catch (err) {
                console.error("Failed to fetch comparison:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchComparison();
    }, []);
    
    return (
        <div className="noise-overlay min-h-screen">
            {/* Header */}
            <section className="py-12 lg:py-16 border-b border-border">
                <div className="container mx-auto px-6 lg:px-12">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">Side by Side</p>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase">
                        Compare Systems
                    </h1>
                    <p className="text-muted-foreground mt-4 max-w-2xl">
                        Compare specifications and features across our equipment lineup to find the right system for your application.
                    </p>
                </div>
            </section>
            
            <div className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                {loading ? (
                    <div className="bg-card border border-border p-8 animate-pulse h-96" />
                ) : comparisonTable ? (
                    <>
                        <Card className="bg-card border border-border rounded-none overflow-hidden" data-testid="comparison-table">
                            <CardContent className="p-0">
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="border-border bg-secondary/50">
                                                <TableHead className="font-mono uppercase text-xs text-muted-foreground w-48 sticky left-0 bg-secondary/50">
                                                    Feature
                                                </TableHead>
                                                {comparisonTable.products.map((product) => (
                                                    <TableHead key={product.product_id} className="font-mono uppercase text-xs text-center min-w-[150px]">
                                                        <Link 
                                                            to={`/products/${product.product_id}`}
                                                            className="hover:text-primary transition-colors"
                                                        >
                                                            {product.product_name}
                                                        </Link>
                                                    </TableHead>
                                                ))}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {comparisonTable.features.map((feature, index) => (
                                                <TableRow key={feature.key} className="border-border hover:bg-secondary/30">
                                                    <TableCell className="font-medium sticky left-0 bg-card">
                                                        {feature.name}
                                                    </TableCell>
                                                    {comparisonTable.products.map((product) => (
                                                        <TableCell key={product.product_id} className="text-center">
                                                            {renderCellValue(product.values[feature.key])}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                            {/* Action Row */}
                                            <TableRow className="border-border bg-secondary/30">
                                                <TableCell className="font-medium sticky left-0 bg-secondary/30">
                                                    Action
                                                </TableCell>
                                                {comparisonTable.products.map((product) => (
                                                    <TableCell key={product.product_id} className="text-center">
                                                        <Link to={`/quote/${product.product_id}`}>
                                                            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none text-xs uppercase tracking-wider">
                                                                Request Quote
                                                            </Button>
                                                        </Link>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                        
                        {/* Help Section */}
                        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card className="bg-card border border-border rounded-none tech-corners">
                                <CardContent className="p-6 lg:p-8">
                                    <h3 className="font-heading font-bold text-xl mb-4">Need Help Choosing?</h3>
                                    <p className="text-muted-foreground mb-4">
                                        Our specialists can help you select the right system based on your specific application, volume requirements, and budget.
                                    </p>
                                    <Link to="/guidance">
                                        <Button variant="outline" className="rounded-none uppercase font-bold tracking-wider">
                                            Get Expert Guidance
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                            
                            <Card className="bg-primary rounded-none tech-corners">
                                <CardContent className="p-6 lg:p-8">
                                    <h3 className="font-heading font-bold text-xl mb-4 text-primary-foreground">Found Your Match?</h3>
                                    <p className="text-primary-foreground/80 mb-4">
                                        Request a quote for any system and we'll get back to you with pricing and availability within 24 hours.
                                    </p>
                                    <Link to="/quote">
                                        <Button className="bg-background text-foreground hover:bg-background/90 rounded-none uppercase font-bold tracking-wider">
                                            Request Quote
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-16">
                        <p className="text-muted-foreground mb-4">No comparison table available yet.</p>
                        <Link to="/products">
                            <Button variant="outline" className="rounded-none">
                                Browse Products
                            </Button>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

// Helper function to render cell values
const renderCellValue = (value) => {
    if (value === "Yes") {
        return <Check className="h-5 w-5 text-green-500 mx-auto" />;
    }
    if (value === "No") {
        return <span className="text-muted-foreground">—</span>;
    }
    return <span className="font-mono text-sm">{value || "—"}</span>;
};

export default ComparePage;
